import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const DailyInflowsOutflowsChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const revenue = metrics?.revenue ?? 0;
                const expenses = metrics?.expenses ?? 0;
                return {
                    date,
                    revenue,
                    expenses: -Math.abs(expenses),
                };
            });
    }, [finance?.dailyBreakdown]);

    const totals = useMemo(() => {
        const totalRevenue = chartData.reduce((sum, d) => sum + d.revenue, 0);
        const totalExpenses = chartData.reduce((sum, d) => sum + Math.abs(d.expenses), 0);
        return { totalRevenue, totalExpenses };
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span>
                <span style={{ color: CHART_COLORS.revenue }}>
                    {formatCompactNumber(totals.totalRevenue, currencySymbol)}
                </span>
                {' / '}
                <span style={{ color: CHART_COLORS.expense }}>
                    {formatCompactNumber(-totals.totalExpenses, currencySymbol)}
                </span>
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            itemStyle={CHART_STYLES.tooltip.itemStyle}
                            formatter={(value: number, name: string) => [
                                formatCurrency(Math.abs(value), currencySymbol),
                                name,
                            ]}
                            labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                        />
                        <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                        <Bar
                            dataKey="revenue"
                            name={t('charts.revenue', 'Revenue')}
                            fill={CHART_COLORS.revenue}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                        <Bar
                            dataKey="expenses"
                            name={t('charts.expenses', 'Expenses')}
                            fill={CHART_COLORS.expense}
                            radius={[0, 0, 3, 3]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
